import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { IngredientKind } from '@sg/graphql-schema';

// ─────────────────────────────────────────────────────────────────────────────

export const useModificationGroupHelperText = (params: Params) => {
  const { isFreeAggregateMessageEnabled } = params;

  const { formatMessage } = useIntl();

  return useCallback(
    (helperParams: HelperParams) => {
      const { freeAggregateQuantity, ingredientKind } = helperParams;

      if (ingredientKind === IngredientKind.Dressing) {
        return formatMessage(messages.dressingsHelperText);
      }

      if (freeAggregateQuantity > 0 && isFreeAggregateMessageEnabled) {
        return formatMessage(messages.cyoToppingsHelperText, {
          quantity: freeAggregateQuantity,
        });
      }
    },
    [formatMessage, isFreeAggregateMessageEnabled],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  cyoToppingsHelperText: {
    defaultMessage: `{quantity, select,
      1 {First one is included}
      2 {First two are included}
      3 {First three are included}
      4 {First four are included}
      5 {First five are included}
      6 {First six are included}
      7 {First seven are included}
      8 {First eight are included}
      9 {First nine are included}
      10 {First ten are included}
      other {Several are included}
    }`,
    description: 'Customization | Free Aggregate | Helper text',
  },
  dressingsHelperText: {
    defaultMessage: 'Portions + details on next page',
    description: 'Customization | Dressings | Helper text',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  isFreeAggregateMessageEnabled: boolean;
};

type HelperParams = {
  freeAggregateQuantity: number;
  ingredientKind: IngredientKind | null | undefined;
};
